import { clsx } from 'clsx'

import {
  I18nShape,
  T,
  useStaticIntl,
} from '@tribeplatform/react-components/i18n'
import { SlateLayout } from '@tribeplatform/react-components/LayoutFrame'
import { Checkbox } from '@tribeplatform/react-ui-kit/Checkbox'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { useCustomizer, useCustomizerNavigation } from '../hooks/index.js'
import { CustomizerActionType } from '../types.js'

type LayoutDetail = {
  withHeader: boolean
  withSidebar: boolean
}

type LayoutCheckoutProps = {
  layout: SlateLayout
  optionKey: 'withHeader' | 'withSidebar'
  additionalClassName: string
  label: string
  spaceId: string
  disabled: boolean
}

const layoutDetails: Record<SlateLayout, LayoutDetail> = {
  [SlateLayout.noHeaderNoSidebar]: {
    withHeader: false,
    withSidebar: false,
  },
  [SlateLayout.headerNoSidebar]: {
    withHeader: true,
    withSidebar: false,
  },
  [SlateLayout.sidebarNoHeader]: {
    withHeader: false,
    withSidebar: true,
  },
  [SlateLayout.headerSidebar]: {
    withHeader: true,
    withSidebar: true,
  },
}

const getLayoutDetails = (layout: SlateLayout): LayoutDetail =>
  layoutDetails[layout]

const getLayoutFromDetails = (detail: LayoutDetail): SlateLayout => {
  return Object.keys(layoutDetails).find(
    key =>
      layoutDetails[key].withHeader === detail.withHeader &&
      layoutDetails[key].withSidebar === detail.withSidebar,
  ) as SlateLayout
}

const layoutOptionsFactory: (options: {
  headerDisabled: boolean
  sidebar1Disabled: boolean
}) => (intl: I18nShape) => Array<{
  key: 'withHeader' | 'withSidebar'
  label: string
  disabled: boolean
}> =
  ({ headerDisabled, sidebar1Disabled }) =>
  ({ $t }) => [
    {
      key: 'withHeader',
      label: $t({ defaultMessage: 'Header', id: 'Generics.Header' }),
      disabled: headerDisabled,
    },
    {
      key: 'withSidebar',
      label: $t({ defaultMessage: 'Sidebar', id: 'Generics.Sidebar' }),
      disabled: sidebar1Disabled,
    },
  ]

const LayoutCheckbox = ({
  layout,
  optionKey,
  label,
  additionalClassName,
  spaceId,
  disabled,
}: LayoutCheckoutProps) => {
  const { dispatch } = useCustomizer()
  const detail = getLayoutDetails(layout)
  const checked = detail[optionKey]
  return (
    <button
      className={clsx(
        additionalClassName,
        'hover:bg-surface-hovered border border-1 rounded-base items-center',
        checked
          ? 'text-content-subdued border-line'
          : 'text-content-subdued border-line-subdued',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
      type="button"
      disabled={disabled}
      onClick={() => {
        dispatch({
          type: CustomizerActionType.NEW_UPDATE,
          payload: {
            spaceId,
            layout: getLayoutFromDetails({
              ...detail,
              [optionKey]: !checked,
            }),
          },
        })
      }}
    >
      <Checkbox checked={checked} onChange={e => e} />
      <span>{label}</span>
    </button>
  )
}

const LayoutCheckboxWithTooltip = (
  props: Omit<LayoutCheckoutProps, 'additionalClassName'>,
) => {
  const { disabled } = props

  if (!disabled) {
    return (
      <LayoutCheckbox
        additionalClassName="flex flex-row col-span-1 py-1 px-2"
        {...props}
      />
    )
  }

  return (
    <Tooltip>
      <Tooltip.Trigger className="flex flex-row col-span-1">
        <LayoutCheckbox
          additionalClassName="flex flex-row w-full py-1 px-2"
          {...props}
        />
      </Tooltip.Trigger>
      <Tooltip.Panel>
        <T
          defaultMessage="Disabled globally from the Navigation section"
          id="Generics.DisabledNavigation"
        />
      </Tooltip.Panel>
    </Tooltip>
  )
}

export const LayoutPicker = ({
  spaceId,
  currentLayout,
}: {
  spaceId: string
  currentLayout: SlateLayout
}) => {
  const {
    navigationSlates: { headerDisabled = false, sidebar1Disabled = false },
  } = useCustomizerNavigation()

  const layoutOptions = useStaticIntl(
    layoutOptionsFactory({
      headerDisabled,
      sidebar1Disabled,
    }),
  )

  return (
    <div className="grid grid-cols-2 gap-2">
      {layoutOptions.map(option => (
        <LayoutCheckboxWithTooltip
          key={option.key}
          optionKey={option.key}
          layout={currentLayout}
          label={option.label}
          spaceId={spaceId}
          disabled={option.disabled}
        />
      ))}
    </div>
  )
}
